import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  ClipboardList, Plus, Search, Filter, Home, Calendar,
  FileText, CheckCircle, Clock, AlertCircle, Camera, User,
  Building, Loader2, Eye, Trash2, MoreVertical
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { API } from "@/App";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: FileText },
  in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-700", icon: Clock },
  awaiting_signature: { label: "Awaiting Signature", color: "bg-amber-100 text-amber-700", icon: AlertCircle },
  complete: { label: "Complete", color: "bg-green-100 text-green-700", icon: CheckCircle }
};

const REPORT_TYPE_CONFIG = {
  inventory: { label: "Inventory", color: "bg-purple-100 text-purple-700" },
  check_in: { label: "Check-In", color: "bg-blue-100 text-blue-700" },
  check_out: { label: "Check-Out", color: "bg-orange-100 text-orange-700" },
  interim_inspection: { label: "Interim Inspection", color: "bg-teal-100 text-teal-700" }
};

export default function InventoryReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  // When opened from a property page, pre-filter to that property and pre-fill
  // the "new report" form so admins can jump straight into creating one.
  const preselectedPropertyId = searchParams.get('property_id') || '';
  const [filterPropertyId, setFilterPropertyId] = useState(preselectedPropertyId);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");
  
  // New report modal
  const [showNewModal, setShowNewModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newReport, setNewReport] = useState({
    property_id: "",
    report_type: "check_in",
    tenancy_id: "",
    tenant_name: "",
    tenant_email: "",
    landlord_name: "",
    landlord_email: "",
    visit_date: format(new Date(), "yyyy-MM-dd"),
    linked_report_id: ""
  });
  const [propertyReports, setPropertyReports] = useState([]);
  const [propertyTenancies, setPropertyTenancies] = useState([]);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchReports();
    fetchProperties();
  }, []);
  
  // If we arrived with ?property_id=... pre-select that property in the
  // "New Report" form so the admin can hit Create immediately.
  useEffect(() => {
    if (preselectedPropertyId) {
      setNewReport(prev => ({ ...prev, property_id: preselectedPropertyId }));
    }
    if (searchParams.get('action') === 'new') {
      setShowNewModal(true);
    }
  }, [preselectedPropertyId]);

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/inventory-reports`, { headers });
      setReports(res.data);
    } catch (error) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const fetchProperties = async () => {
    try {
      const res = await axios.get(`${API}/lettings/properties`, { headers });
      setProperties(res.data.properties || []);
    } catch (error) {
      console.error("Failed to load properties");
    }
  };

  const fetchPropertyReports = async (propertyId) => {
    try {
      const res = await axios.get(`${API}/inventory-reports/property/${propertyId}/reports`, { headers });
      // Filter to only show check-in and inventory reports
      const linkableReports = res.data.filter(r => 
        r.report_type === 'check_in' || r.report_type === 'inventory'
      );
      setPropertyReports(linkableReports);
    } catch (error) {
      setPropertyReports([]);
    }
  };

  const fetchPropertyTenancies = async (propertyId) => {
    try {
      const res = await axios.get(`${API}/lettings/tenants?status=active`, { headers });
      // Filter tenants for this specific property
      const tenanciesForProperty = (res.data.tenants || []).filter(t => t.property_id === propertyId);
      setPropertyTenancies(tenanciesForProperty);
    } catch (error) {
      setPropertyTenancies([]);
    }
  };

  const handleTenancySelect = (tenancyId) => {
    const tenancy = propertyTenancies.find(t => t.tenant_id === tenancyId);
    if (tenancy) {
      setNewReport(prev => ({
        ...prev,
        tenancy_id: tenancyId,
        tenant_name: tenancy.name || "",
        tenant_email: tenancy.email || "",
        landlord_name: tenancy.landlord?.name || "",
        landlord_email: tenancy.landlord?.email || ""
      }));
    } else {
      // Clear tenancy link if "manual" is selected
      setNewReport(prev => ({
        ...prev,
        tenancy_id: "",
        tenant_name: "",
        tenant_email: "",
        landlord_name: "",
        landlord_email: ""
      }));
    }
  };

  const handleCreateReport = async () => {
    if (!newReport.property_id) {
      toast.error("Please select a property");
      return;
    }

    setCreating(true);
    try {
      const payload = { ...newReport };
      if (!payload.linked_report_id) delete payload.linked_report_id;
      if (!payload.tenancy_id) delete payload.tenancy_id;
      
      const res = await axios.post(`${API}/inventory-reports`, payload, { headers });
      const cloned = res.data?.cloned_summary;
      if (cloned && (cloned.rooms || cloned.items)) {
        toast.success(`Report created — cloned ${cloned.rooms} rooms / ${cloned.items} items from the linked report. Just mark any changes.`);
      } else {
        toast.success("Report created successfully");
      }
      setShowNewModal(false);
      setNewReport({
        property_id: "",
        report_type: "check_in",
        tenancy_id: "",
        tenant_name: "",
        tenant_email: "",
        landlord_name: "",
        landlord_email: "",
        visit_date: format(new Date(), "yyyy-MM-dd"),
        linked_report_id: ""
      });
      setPropertyReports([]);
      setPropertyTenancies([]);
      navigate(`/inventory/report/${res.data.report_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create report");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!confirm("Are you sure you want to delete this report? This cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/inventory-reports/${reportId}`, { headers });
      toast.success("Report deleted");
      fetchReports();
    } catch (error) {
      toast.error("Failed to delete report");
    }
  };

  const filteredReports = reports.filter(report => {
    const matchesSearch = 
      report.property_address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.property_postcode?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === "all" || report.status === filterStatus;
    const matchesType = filterType === "all" || report.report_type === filterType;
    const matchesProperty = !filterPropertyId || report.property_id === filterPropertyId;
    
    return matchesSearch && matchesStatus && matchesType && matchesProperty;
  });

  // Stats
  const stats = {
    total: reports.length,
    draft: reports.filter(r => r.status === "draft").length,
    inProgress: reports.filter(r => r.status === "in_progress").length,
    awaiting: reports.filter(r => r.status === "awaiting_signature").length,
    complete: reports.filter(r => r.status === "complete").length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-14 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Inventory Reports</h1>
                <p className="text-sm text-muted-foreground">Check-ins, Check-outs & Inspections</p>
              </div>
            </div>
            <Button onClick={() => setShowNewModal(true)} className="bg-purple-600 hover:bg-purple-700">
              <Plus className="w-4 h-4 mr-2" />
              New Report
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Property filter banner */}
        {filterPropertyId && (() => {
          const prop = properties.find(p => p.property_id === filterPropertyId);
          return (
            <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5">
              <div className="flex items-center gap-2 min-w-0 text-sm text-blue-900">
                <Home className="w-4 h-4 shrink-0" />
                <span className="truncate">
                  Showing reports for: <strong>{prop?.address || filterPropertyId}</strong>
                </span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="text-blue-700 hover:bg-blue-100 shrink-0"
                onClick={() => setFilterPropertyId('')}
                data-testid="clear-property-filter"
              >
                Show all properties
              </Button>
            </div>
          );
        })()}
        
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Reports</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <FileText className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Drafts</p>
                  <p className="text-2xl font-bold text-slate-600">{stats.draft}</p>
                </div>
                <FileText className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.inProgress}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Awaiting Signature</p>
                  <p className="text-2xl font-bold text-amber-600">{stats.awaiting}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Complete</p>
                  <p className="text-2xl font-bold text-green-600">{stats.complete}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by address, postcode, or tenant..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="awaiting_signature">Awaiting Signature</SelectItem>
                  <SelectItem value="complete">Complete</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="inventory">Inventory</SelectItem>
                  <SelectItem value="check_in">Check-In</SelectItem>
                  <SelectItem value="check_out">Check-Out</SelectItem>
                  <SelectItem value="interim_inspection">Interim Inspection</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Reports List */}
        {filteredReports.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="p-12 text-center">
              <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No Reports Found</h3>
              <p className="text-muted-foreground mb-4">
                {searchTerm || filterStatus !== "all" || filterType !== "all"
                  ? "Try adjusting your search or filters"
                  : "Get started by creating your first inventory report"}
              </p>
              {!searchTerm && filterStatus === "all" && filterType === "all" && (
                <Button onClick={() => setShowNewModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Report
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredReports.map((report) => {
              const statusConfig = STATUS_CONFIG[report.status] || STATUS_CONFIG.draft;
              const typeConfig = REPORT_TYPE_CONFIG[report.report_type] || REPORT_TYPE_CONFIG.check_in;
              const StatusIcon = statusConfig.icon;

              return (
                <Card 
                  key={report.report_id} 
                  className="bg-white hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/inventory/report/${report.report_id}`)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
                          <Home className="w-6 h-6 text-slate-500" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold">{report.property_address}</h3>
                            <Badge className={typeConfig.color}>{typeConfig.label}</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-muted-foreground">
                            <span>{report.property_postcode}</span>
                            {report.tenant_name && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {report.tenant_name}
                                </span>
                              </>
                            )}
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {report.visit_date ? format(new Date(report.visit_date), "dd MMM yyyy") : "No date"}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right text-sm">
                          <div className="flex items-center gap-4 text-muted-foreground">
                            <span>{report.room_count || 0} rooms</span>
                            <span>{report.item_count || 0} items</span>
                            <span className="flex items-center gap-1">
                              <Camera className="w-3 h-3" />
                              {report.photo_count || 0}
                            </span>
                          </div>
                        </div>
                        <Badge className={`${statusConfig.color} flex items-center gap-1`}>
                          <StatusIcon className="w-3 h-3" />
                          {statusConfig.label}
                        </Badge>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/inventory/report/${report.report_id}`);
                            }}>
                              <Eye className="w-4 h-4 mr-2" />
                              View Report
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteReport(report.report_id);
                              }}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* New Report Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Report</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Property *</Label>
              <Select 
                value={newReport.property_id} 
                onValueChange={(v) => {
                  setNewReport(prev => ({ 
                    ...prev, 
                    property_id: v, 
                    linked_report_id: "",
                    tenancy_id: "",
                    tenant_name: "",
                    tenant_email: "",
                    landlord_name: "",
                    landlord_email: ""
                  }));
                  fetchPropertyReports(v);
                  fetchPropertyTenancies(v);
                }}
              >
                <SelectTrigger className="mt-1" data-testid="property-select">
                  <SelectValue placeholder="Select a property..." />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.property_id} value={prop.property_id}>
                      {prop.address}, {prop.postcode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Link to Tenancy */}
            {newReport.property_id && (
              <div>
                <Label>Link to Tenancy</Label>
                <Select 
                  value={newReport.tenancy_id} 
                  onValueChange={handleTenancySelect}
                >
                  <SelectTrigger className="mt-1" data-testid="tenancy-select">
                    <SelectValue placeholder="Select tenancy or enter manually..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Enter details manually</SelectItem>
                    {propertyTenancies.map((t) => (
                      <SelectItem key={t.tenant_id} value={t.tenant_id}>
                        {t.name} {t.tenancy_reference && `(${t.tenancy_reference})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {propertyTenancies.length === 0 && (
                  <p className="text-xs text-muted-foreground mt-1">
                    No active tenancies found for this property
                  </p>
                )}
              </div>
            )}

            <div>
              <Label>Report Type *</Label>
              <Select 
                value={newReport.report_type} 
                onValueChange={(v) => setNewReport(prev => ({ ...prev, report_type: v, linked_report_id: "" }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inventory">Inventory (Baseline)</SelectItem>
                  <SelectItem value="check_in">Check-In</SelectItem>
                  <SelectItem value="check_out">Check-Out</SelectItem>
                  <SelectItem value="interim_inspection">Interim Inspection</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Link to Check-In (for Check-Out reports) */}
            {newReport.report_type === "check_out" && newReport.property_id && (
              <div>
                <Label>Clone Structure From (Check-In / Inventory)</Label>
                <Select 
                  value={newReport.linked_report_id || "none"} 
                  onValueChange={(v) => setNewReport(prev => ({ ...prev, linked_report_id: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="mt-1" data-testid="linked-report-select">
                    <SelectValue placeholder="Select check-in to clone & compare..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Start from blank (no clone)</SelectItem>
                    {propertyReports.map((r) => (
                      <SelectItem key={r.report_id} value={r.report_id}>
                        {r.report_type_label} - {r.visit_date ? format(new Date(r.visit_date), "dd MMM yyyy") : "No date"} 
                        {r.tenant_name && ` (${r.tenant_name})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {propertyReports.length === 0 ? (
                  <p className="text-xs text-muted-foreground mt-1">
                    No previous reports found for this property — the check-out will start blank.
                  </p>
                ) : (
                  <p className="text-xs text-emerald-700 mt-1">
                    Rooms + items will be copied from the chosen report. Just mark each item as <strong>No change</strong>, <strong>Wear & tear</strong> or <strong>Damage</strong>.
                  </p>
                )}
              </div>
            )}

            <div>
              <Label>Visit Date</Label>
              <Input
                type="date"
                value={newReport.visit_date}
                onChange={(e) => setNewReport(prev => ({ ...prev, visit_date: e.target.value }))}
                className="mt-1"
              />
            </div>

            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm">Tenant Details</h4>
                {newReport.tenancy_id && (
                  <Badge variant="secondary" className="text-xs">From Tenancy</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tenant Name</Label>
                  <Input
                    placeholder="Enter tenant name"
                    value={newReport.tenant_name}
                    onChange={(e) => setNewReport(prev => ({ ...prev, tenant_name: e.target.value }))}
                    className="mt-1"
                    data-testid="tenant-name-input"
                  />
                </div>
                <div>
                  <Label>Tenant Email</Label>
                  <Input
                    type="email"
                    placeholder="tenant@email.com"
                    value={newReport.tenant_email}
                    onChange={(e) => setNewReport(prev => ({ ...prev, tenant_email: e.target.value }))}
                    className="mt-1"
                    data-testid="tenant-email-input"
                  />
                </div>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-sm">Landlord Details</h4>
                {newReport.tenancy_id && (
                  <Badge variant="secondary" className="text-xs">From Tenancy</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Landlord Name</Label>
                  <Input
                    placeholder="Enter landlord name"
                    value={newReport.landlord_name}
                    onChange={(e) => setNewReport(prev => ({ ...prev, landlord_name: e.target.value }))}
                    className="mt-1"
                    data-testid="landlord-name-input"
                  />
                </div>
                <div>
                  <Label>Landlord Email</Label>
                  <Input
                    type="email"
                    placeholder="landlord@email.com"
                    value={newReport.landlord_email}
                    onChange={(e) => setNewReport(prev => ({ ...prev, landlord_email: e.target.value }))}
                    className="mt-1"
                    data-testid="landlord-email-input"
                  />
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800 mt-4">
              <strong>Signing Workflow:</strong> Tenant signs first → Landlord signs → Everyone gets completed PDF
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewModal(false)}>Cancel</Button>
            <Button onClick={handleCreateReport} disabled={creating} className="bg-purple-600 hover:bg-purple-700" data-testid="create-report-btn">
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
